import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, X, ShieldCheck, AlertTriangle, MapPin, Target } from "lucide-react";
import type { DecisionOption, CoverageState, ConfidenceLevel, TrustProof } from "@/lib/decisionEngine";
import type { ParsedQuery } from "@/lib/queryParser";

const confidenceText: Record<ConfidenceLevel, string> = {
  high: "High confidence",
  medium: "Moderate confidence",
  low: "Limited confidence",
  insufficient: "Insufficient data",
};

interface Props {
  query: string;
  parsed: ParsedQuery;
  winner: DecisionOption;
  proof: TrustProof;
  coverage: CoverageState;
}

export default function DecisionReceipt({ query, parsed, winner, proof, coverage }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-secondary text-secondary-foreground text-xs font-body hover:border-primary/30 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" />
        View decision breakdown
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm px-4"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl border border-border bg-card p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-display font-bold text-foreground">Decision Breakdown</h3>
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <p className="text-xs text-muted-foreground font-body mb-1">Query</p>
                  <p className="text-sm text-foreground font-body">{query}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground font-body mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Location
                    </p>
                    <p className="text-sm text-foreground font-body">{parsed.country || "Global"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-body mb-1 flex items-center gap-1">
                      <Target className="w-3 h-3" /> Priority
                    </p>
                    <p className="text-sm text-foreground font-body capitalize">{parsed.priorities[0] || "Overall quality"}</p>
                  </div>
                </div>

                <div className="px-4 py-3 rounded-xl border border-primary/20 bg-primary/5">
                  <p className="text-xs text-primary font-body mb-1">Winner</p>
                  <p className="text-base font-display font-bold text-foreground">{winner.productName}</p>
                  <p className="text-sm text-muted-foreground font-body mt-1">{winner.price} / {winner.deliveryTime}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground font-body mb-1">Confidence</p>
                  <p className="text-sm text-foreground font-body">{confidenceText[winner.confidence]}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground font-body mb-2">Why it won</p>
                  <ul className="space-y-1.5">
                    {winner.audit.whyWon.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground font-body">
                        <ShieldCheck className="w-3 h-3 text-score-high shrink-0 mt-0.5" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground font-body mb-2">What was removed</p>
                  <ul className="space-y-1.5">
                    {winner.audit.filteredOut.slice(0, 4).map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground font-body">
                        <AlertTriangle className="w-3 h-3 text-score-low shrink-0 mt-0.5" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="px-3 py-2 rounded-lg bg-muted/30 border border-border/30">
                  <p className="text-xs text-muted-foreground font-body">{winner.audit.scope}</p>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground font-body">
                  <span className="capitalize">{coverage} coverage</span>
                  <span>{proof.sourcesChecked} sources</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
